import React from 'react';
import { createPortal } from 'react-dom';
import './infotip.css';

/* Infobulle noire · composant unique (brief « survol & infobulles », section 9).
 * Un chiffre = une source : titre + badge fiabilité, 4 lignes SOURCE/PÉRIODE/FORMULE/FIABILITÉ, pied, flèche.
 * S'affiche au survol (et focus) du texte uniquement. align : 'c' (centré) · 'l' (bord gauche) · 'r' (bord droit).
 * side : 'up' (par défaut, s'ouvre vers le haut) · 'down' (1ʳᵉ ligne d'une liste → vers le bas). */
const BADGE = { reel: 'b-reel', theorique: 'b-theo', estime: 'b-est', provisoire: 'b-prov', gris: 'b-gris' };
const BADGE_LABEL = { reel: 'réel', theorique: 'théorique', estime: 'estimé', provisoire: 'provisoire', gris: 'pas de donnée' };

export const FIAB_BRUT = { reel: 'réel', theorique: 'théorique', estime: 'estimé', saisi: 'saisi', provisoire: 'provisoire', a_verifier: 'à vérifier' };  // LOT 55 · jamais la clé brute « reel »
export const InfoTip = ({ titre, fiabilite = 'reel', badgeLabel, source, periode, formule, fiabiliteLigne, alerte, pied = 'Touchez pour le détail', align = 'c', side = 'down' }) => {
  // LOT 55 · jamais une clé brute (« gris », « vide », « reel ») ni un mot répété (« règle règle »)
  const brut = String(fiabiliteLigne || '');
  const tag = badgeLabel || BADGE_LABEL[fiabilite] || FIAB_BRUT[fiabilite] || (brut.startsWith('—') ? '—' : 'saisi');
  const ligne = (FIAB_BRUT[brut] || brut).replace(/^—\s*·\s*/, tag === '—' ? '' : '— · ');
  const anchorRef = React.useRef(null);
  const tipRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  // LOT 59 · la carte vit dans <body> (position fixe) : jamais rognée par une tuile, toujours dans l'écran
  const [pos, setPos] = React.useState(null);
  React.useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const host = anchorRef.current?.parentElement; const t = tipRef.current;
    if (!host || !t) return;
    const h = host.getBoundingClientRect(); const w = t.offsetWidth; const th = t.offsetHeight;
    const vw = window.innerWidth; const vh = window.innerHeight;
    const cx = h.left + h.width / 2;
    const want = align === 'l' ? h.left : align === 'r' ? h.right - w : cx - w / 2;
    const x = Math.round(Math.min(Math.max(want, 8), Math.max(8, vw - 8 - w)));
    const bas = side !== 'up' ? h.bottom + 10 + th <= vh - 8 || h.top - 10 - th < 8 : h.top - 10 - th < 8;
    const y = Math.round(bas ? h.bottom + 10 : h.top - 10 - th);
    setPos({ x, y, bas, ax: Math.round(Math.min(Math.max(cx - x, 18), w - 18)) });
  }, [open, align, side]);
  React.useEffect(() => { if (!open) return undefined; const k = (e) => e.key === 'Escape' && setOpen(false); document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [open]);
  // Règle 1.3 · la carte source n'existe dans le DOM qu'au survol / focus.
  // Sur une page à 1 500 chiffres, cela évite d'empiler 1 500 cartes invisibles.
  React.useEffect(() => {
    const host = anchorRef.current?.parentElement;
    if (!host) return;
    // LOT 60 · jamais d'ouverture seule : un vrai geste de souris, ou le focus clavier
    const on = () => setOpen(true);
    const off = () => setOpen(false);
    const clavier = (e) => { if (e.target?.matches?.(':focus-visible')) setOpen(true); };
    host.addEventListener('mousemove', on);
    host.addEventListener('mouseleave', off);
    host.addEventListener('focusin', clavier);
    host.addEventListener('focusout', off);
    return () => {
      host.removeEventListener('mousemove', on);
      host.removeEventListener('mouseleave', off);
      host.removeEventListener('focusin', clavier);
      host.removeEventListener('focusout', off);
    };
  }, []);
  return (
    <>
      <span ref={anchorRef} aria-hidden="true" style={{ display: 'none' }} />
      {open && createPortal(
        <div ref={tipRef} className={`itip is-fixe itip-${pos?.bas === false ? 'up' : 'down'} is-open`} style={{ '--x': `${pos?.x ?? -9999}px`, '--y': `${pos?.y ?? 0}px`, '--ax': `${pos?.ax ?? 0}px`, visibility: pos ? 'visible' : 'hidden' }} role="tooltip" data-testid="infotip">
          <h4>{titre}</h4>
          <dl>
            {source != null && <><dt>Source</dt><dd>{source}</dd></>}
            {periode != null && <><dt>Période</dt><dd>{periode}</dd></>}
            <dt>Formule</dt><dd>{formule != null && formule !== titre ? formule : (fiabilite === 'gris' ? 'aucun calcul · la donnée n’existe pas encore' : 'lu tel quel dans la source')}</dd>
            <dt>Fiabilité</dt><dd><em className={`itip-tag ${BADGE[fiabilite] || 'b-gris'}`}>{tag}</em> {ligne && ligne !== tag ? ligne : null}{alerte ? <> · <u>{alerte}</u></> : null}</dd>
          </dl>
          <div className="itip-go">{pied} ›</div>
        </div>, document.body
      )}
    </>
  );
};

/* Enveloppe à poser autour d'un chiffre : le survol ouvre l'InfoTip ancrée dessus. */
export const HasTip = ({ children, className = '', testId, ...tip }) => (
  <span className={`has-itip ${className}`} tabIndex={0} data-testid={testId}>
    {children}
    <InfoTip {...tip} />
  </span>
);
